import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { RadioField } from '@openx/components-core/lib/RadioField/RadioField.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { type DealApi, type PmpDealType, pmpDealType } from '@openx/types/deal.ts';
import { priceFormat } from '@openx/utils/lib/priceFormat.ts';
import { useTranslation } from 'react-i18next';
import type { DealDetailsSectionProps } from '../../../types.ts';
import { useFixedCpmEnabledOptions } from '../../DealForm/hooks/useFixedCpmEnabledOptions.ts';
import { getDealsLabels } from '../../DealForm/utils/dealFormUtils.ts';
import { useDealContext } from '../../utils/context/DealContext.tsx';

const StyledBox = styled(Box)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

export type FloorAndDurationSectionDeal = Pick<
  DealApi,
  'currency' | 'deal_price' | 'end_date' | 'fixed_cpm_enabled' | 'pmp_deal_type' | 'start_date' | 'use_market_floor'
>;

export function DealFloorAndDurationSection<T extends FloorAndDurationSectionDeal>({
  deal,
  historyMode,
  showPacing,
}: DealDetailsSectionProps<T>) {
  const { t } = useTranslation();
  const labels = getDealsLabels(t);
  const { useCurrenciesFetch, isOa } = useDealContext();
  const { fixedCpmEnabledOptions } = useFixedCpmEnabledOptions();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { loading: currenciesLoading, mapById: currencies } = useCurrenciesFetch();

  const { currency, deal_price, end_date, fixed_cpm_enabled, pmp_deal_type, start_date, use_market_floor } = deal;

  const dealTypesWithVisibleMarketFloor: PmpDealType[] = [pmpDealType.PrivateAuction, pmpDealType.WithinOpenAuction];
  const shouldDisplayMarketFloor = !isBefInstance && !isOa && dealTypesWithVisibleMarketFloor.includes(pmp_deal_type);

  return (
    <FormSection divider historyMode={historyMode} title={showPacing ? t('Deal Floor') : t('Deal Floor & Duration')}>
      {isBefInstance && (
        <RadioField
          aria-label={t('Deal floor type')}
          isDisabled
          options={fixedCpmEnabledOptions}
          row
          sx={{ mb: 2 }}
          value={fixed_cpm_enabled}
        />
      )}

      <FieldDetails
        loading={currenciesLoading}
        title={labels.currency}
        value={currencies?.[currency]?.name || currency}
      />

      <FieldDetails title={labels.deal_price} value={priceFormat(deal_price, currency)?.toString()} />

      {shouldDisplayMarketFloor && (
        <StyledBox>
          <IndentedSections>
            <FieldDetails
              checkbox
              title={labels.use_market_floor}
              value={use_market_floor ? parseInt(use_market_floor, 10) : null}
            />
          </IndentedSections>
        </StyledBox>
      )}

      {!showPacing && (
        <Grid
          container
          sx={{
            mb: 2,
          }}
        >
          <Grid size={3}>
            <FieldDetails
              title={labels.start_date}
              value={start_date}
              dateFormat={isOa ? 'yyyy-MM-dd' : 'yyyy-MM-dd hh:mm a'}
            />
          </Grid>
          <FieldDetails
            title={labels.end_date}
            value={end_date}
            dateFormat={isOa ? 'yyyy-MM-dd' : 'yyyy-MM-dd hh:mm a'}
          />
        </Grid>
      )}
    </FormSection>
  );
}
